import { useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { setViewPort } from '@/actions/index'
import useMapInstance from '@/hooks/useMapInstance'

/**
 * Interface defining the parameters for zooming to a geographical point
 */
interface ZoomToPointParams {
  /**
   * Coordinates in the format [longitude, latitude]
   */
  coordinates: [number, number]
  /**
   * Zoom level to set (1-22)
   * @default 14
   */
  zoom?: number
  /**
   * Duration of the transition animation in milliseconds
   * @default 1000
   */
  // transitionDuration?: number
  /**
   * Optional callback function to execute when the transition completes
   */
  onComplete?: () => void
}

type ViewportState = {
  latitude?: number
  longitude?: number
  zoom?: number
  bearing?: number
  pitch?: number
  width?: number
  height?: number
  [key: string]: unknown
}

/**
 * A hook that provides functionality to smoothly zoom the map to specified coordinates
 * Uses MapLibre GL JS easeTo for smooth direct transitions
 *
 * @returns An object containing the zoomToPoint function
 *
 * @example
 * ```tsx
 * const { zoomToPoint } = useZoomToPoint();
 *
 * // Zoom to New York
 * const handleClick = () => {
 *   zoomToPoint({
 *     coordinates: [-74.006, 40.7128],
 *     zoom: 12
 *   });
 * };
 * ```
 */
const useZoomToPoint = () => {
  const dispatch = useDispatch()
  const store = useStore()
  const { map, isReady } = useMapInstance()

  const zoomToPoint = useCallback(
    ({ coordinates, zoom = 14, onComplete }: ZoomToPointParams) => {
      if (!coordinates) {
        console.warn('useZoomToPoint: No coordinates provided')
        onComplete?.()
        return
      }

      const [longitude, latitude] = coordinates

      if (
        typeof longitude !== 'number' ||
        typeof latitude !== 'number' ||
        isNaN(longitude) ||
        isNaN(latitude)
      ) {
        console.warn('useZoomToPoint: Invalid coordinates:', coordinates)
        onComplete?.()
        return
      }

      if (!isReady || !map) {
        console.warn('useZoomToPoint: Map instance not available')
        onComplete?.()
        return
      }

      try {
        // Use MapLibre GL JS easeTo for smooth direct transition
        // flyTo uses van Wijk algorithm which zooms out then back in
        // easeTo provides direct linear interpolation matching old FlyToInterpolator behavior
        map.easeTo({
          center: [longitude, latitude],
          zoom,
          duration: 800,
          essential: true,
        })

        // Keep Redux/controlled viewState in sync with the imperative camera
        // move so the next zoom gesture doesn't snap back (MAM-857). Read the
        // camera the map actually settled on rather than the requested
        // destination — if the animation was interrupted, they can differ.
        // void: map.once's overload includes Promise when called without a listener.
        void map.once('moveend', () => {
          queueMicrotask(() => {
            const center = map.getCenter()
            const current =
              (store.getState() as { viewport?: ViewportState }).viewport ?? {}
            dispatch(
              setViewPort({
                ...current,
                longitude: center.lng,
                latitude: center.lat,
                zoom: map.getZoom(),
                bearing: map.getBearing(),
                pitch: map.getPitch(),
              })
            )
            onComplete?.()
          })
        })
      } catch (err) {
        console.error('Error in zoomToPoint:', err)
        onComplete?.()
      }
    },
    [map, isReady, dispatch, store]
  )

  return { zoomToPoint }
}

export default useZoomToPoint
